import { useEffect, useState, type ReactNode } from 'react';

import { LoadingIndicator } from '../components/Spinner';
import { LoginPage } from '../features/auth/LoginPage';
import { TenantSelectPage } from '../features/auth/TenantSelectPage';
import { tryRefresh } from '../lib/apiClient';
import { getSession, subscribeToSession, type SessionState } from '../lib/session';

type Phase = 'checking' | 'ready';

/**
 * Entscheidet, was der Anwender ueberhaupt zu sehen bekommt.
 *
 * Beim Start ist kein Zugangstoken da -- es liegt im Arbeitsspeicher und ist
 * beim Neuladen fort (siehe `lib/session.ts`). Die Sitzung besteht aber
 * womoeglich weiter, weil das Erneuerungstoken im `httpOnly`-Cookie liegt.
 * Deshalb wird beim Start **einmal** erneuert, bevor entschieden wird.
 *
 * Ohne diesen Schritt landete jeder Anwender nach jedem Neuladen auf der
 * Anmeldemaske -- obwohl seine Sitzung noch vierzehn Tage gueltig ist.
 *
 * Der Mandantenzustand wird ebenfalls hier abgefangen: Ein Bearbeiter mit
 * mehreren Freigaben sieht zuerst die Auswahl. Ohne gewaehlten Mandanten
 * antwortet jeder fachliche Endpunkt mit 403; die Auswahl vorzuschalten ist
 * deshalb nicht Bequemlichkeit, sondern der einzige gangbare Weg.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('checking');
  const [session, setSessionState] = useState<SessionState | null>(getSession);

  useEffect(() => subscribeToSession(setSessionState), []);

  useEffect(() => {
    let cancelled = false;

    if (getSession()) {
      setPhase('ready');
      return;
    }

    void tryRefresh().finally(() => {
      if (!cancelled) setPhase('ready');
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (phase === 'checking') {
    return <LoadingIndicator />;
  }

  if (!session) {
    return <LoginPage />;
  }

  if (session.requiresTenantSelection || session.tenantId === null) {
    return <TenantSelectPage />;
  }

  return <>{children}</>;
}
